import { HGenerateFileData, IFileData } from "@rf-l1/file/helpers/create-file-data";
import { useFormContext } from "react-hook-form";
import { CPropertyFormKeys } from "../util/schemas/property-schema/propertySchema";
import { useParams } from "react-router-dom";
import {
  useUploadImageByIdMutation,
  useDeleteImageByIdMutation
} from "@/src-refactor/modules/store/qrtk/services/realestate/realestate.qrtk";
import { onSendEventBlockerEnd, onSendEventBlockerStart } from "./useUploadingBlocker";

/**
 * Modo edición (no draft): sube imágenes a /v3/admin/realestates/:id/images
 * Eliminar: solo remueve del formulario, no llama API.
 */
const useManagerImagesEdit = () => {
  const { setValue, getValues } = useFormContext();
  const { id } = useParams();
  const realEstateId = Number(id);
  const [uploadImage] = useUploadImageByIdMutation();
  const [deleteImageById] = useDeleteImageByIdMutation();

  const uploadImageEdit = async (data: IFileData) => {
    return new Promise(async (resolve, reject) => {
      try {
        // Solo subir si es un File; si viene como URL (edición con recurso existente), no hacer nada
        if (!(data?.file instanceof File)) {
          resolve({ url: data?.url ?? "", id: data?.id?.toString?.() ?? "" });
          return;
        }

        onSendEventBlockerStart()

        const res = await uploadImage({
          params: { id: realEstateId },
          body: {
            image: data.file as File
          }
        }).unwrap();

        const uploadedUrl = (res as any)?.url ?? (res as any)?.data?.path ?? "";
        const uploadedId = (res as any)?.id ?? (res as any)?.data?.id ?? 0;
        const picture = HGenerateFileData({
          file: uploadedUrl,
          id: Number(uploadedId) ?? 0,
          comment: ""
        });

        const newArray = [...getValues(CPropertyFormKeys.images_property)];
        const pos = newArray.findIndex((item: IFileData) => item.id === data.id);
        newArray.splice(pos, 1, picture as IFileData);

        setValue(CPropertyFormKeys.images_property, [...newArray]);
        resolve({ url: picture?.url ?? "", id: picture?.id?.toString?.() ?? "" });
      } catch (err) {
        reject(err);
      } finally {
        onSendEventBlockerEnd()
      }
    });
  };

  const deleteImageEdit = async (index: number) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const fields = getValues(CPropertyFormKeys.images_property) as IFileData[];
        const target = fields[index];

        // Si tiene id numérico, intentar borrar en backend
        const numericId = Number(target?.id);
        if (!Number.isNaN(numericId) && numericId > 0) {
          await deleteImageById({
            params: { id: realEstateId, imageId: numericId }
          }).unwrap();
        }

        setValue(
          CPropertyFormKeys.images_property,
          fields.filter((_, i) => i !== index)
        );
        resolve();
      } catch (e) {
        reject(e);
      }
    });
  };

  const deleteAllImagesEdit = async (current?: IFileData[]) => {
    const fields = (current ?? (getValues(CPropertyFormKeys.images_property) as IFileData[])) || [];
    const remaining: IFileData[] = [];
    const failedDeletions: Array<{ id: number; error: any }> = [];

    for (let i = 0; i < fields.length; i++) {
      const file = fields[i];
      const numericId = Number(file?.id);

      if (!Number.isNaN(numericId) && numericId > 0) {
        try {
          await deleteImageById({ params: { id: realEstateId, imageId: numericId } }).unwrap();
          // ok eliminado
        } catch (error) {
          failedDeletions.push({ id: numericId, error });
          remaining.push(file);
        }
      }
      // archivos locales sin id se eliminan implícitamente (no se agregan a remaining)
    }

    setValue(CPropertyFormKeys.images_property, remaining);
    return { remaining, failedDeletions };
  };

  return {
    uploadImageEdit,
    deleteImageEdit,
    deleteAllImagesEdit
  };
};

export default useManagerImagesEdit;
